/**
 * CareStart Studio — nurse-easy variant creation form (Phase 2).
 *
 * WHAT: Short paper-form UI to name, aim, and deploy a CareStart variant with shareable link + QR.
 * WHY: Clinic Managers create screenings without developer involvement.
 * INVARIANT: Requires linked facility (`userData.location`); slug becomes document ID.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { QRCodeSVG } from 'qrcode.react';
import { CARESTART_AIM_OPTIONS } from '../constants/careStartAimOptions';
import {
  createCareStartVariant,
  generateSlugFromDisplayName,
  isValidVariantSlug,
} from '../../../services/carestart/careStartVariantService';
import { contentCardSx } from '../../../styles/standaloneLayoutTokens';

function buildShareUrl(slug) {
  if (typeof window === 'undefined') return `/carestart/${slug}`;
  return `${window.location.origin}/carestart/${slug}`;
}

export default function CareStartVariantForm({ userData, onCreated }) {
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugEditing, setSlugEditing] = useState(false);
  const [aimKey, setAimKey] = useState('patient_self_triage');
  const [aimCustomText, setAimCustomText] = useState('');
  const [audienceMode, setAudienceMode] = useState('patient');
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (slugTouched) return;
    const generated = generateSlugFromDisplayName(displayName);
    setSlug(generated);
  }, [displayName, slugTouched]);

  const shareUrl = useMemo(() => (slug ? buildShareUrl(slug) : ''), [slug]);

  const resetForm = useCallback(() => {
    setDisplayName('');
    setSlug('');
    setSlugTouched(false);
    setSlugEditing(false);
    setAimKey('patient_self_triage');
    setAimCustomText('');
    setAudienceMode('patient');
    setCheckInEnabled(false);
    setSuccess(null);
    setError(null);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);

      if (!isValidVariantSlug(slug)) {
        setError(
          'Your link name must be 3–30 characters and use only lowercase letters, numbers, and hyphens.'
        );
        return;
      }

      setSubmitting(true);
      try {
        const createdSlug = await createCareStartVariant(
          {
            displayName,
            variantSlug: slug,
            aimKey,
            aimCustomText: aimKey === 'custom' ? aimCustomText : null,
            audienceMode,
            checkInEnabled: audienceMode === 'patient' && checkInEnabled,
          },
          userData
        );
        setSuccess({ slug: createdSlug, url: buildShareUrl(createdSlug) });
        onCreated?.();
      } catch (err) {
        setError(err?.message || 'Could not create your CareStart variant.');
      } finally {
        setSubmitting(false);
      }
    },
    [
      slug,
      displayName,
      aimKey,
      aimCustomText,
      audienceMode,
      checkInEnabled,
      userData,
      onCreated,
    ]
  );

  const handleCopy = useCallback(async () => {
    if (!success?.url) return;
    try {
      await navigator.clipboard.writeText(success.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('[CareStartVariantForm] copy failed', err);
    }
  }, [success?.url]);

  if (success) {
    return (
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
        <Stack alignItems="center" spacing={2} textAlign="center">
          <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
          <Typography variant="h6" fontWeight={700}>
            Your CareStart screening is live
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Share this link with patients or staff. They can open it on any phone — no app
            required.
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              width: '100%',
              maxWidth: 420,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Your shareable link
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {success.url}
              </Typography>
              <IconButton onClick={handleCopy} aria-label="Copy shareable link">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Stack>
            {copied ? (
              <Typography variant="caption" color="success.main">
                Copied to clipboard
              </Typography>
            ) : null}
          </Box>
          <QRCodeSVG value={success.url} size={160} level="M" includeMargin />
          <Typography variant="caption" color="text.secondary">
            Screenshot or print this QR code for your front desk
          </Typography>
          <Button variant="outlined" onClick={resetForm}>
            Create another variant
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }} component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Create a new screening
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={3}>
        <TextField
          label="What do you want to call this?"
          placeholder="e.g. ED Intake Triage"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          required
        />

        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
            What is this conversation for?
          </FormLabel>
          <RadioGroup value={aimKey} onChange={(e) => setAimKey(e.target.value)}>
            <Stack spacing={1}>
              {CARESTART_AIM_OPTIONS.map((opt) => (
                <Paper
                  key={opt.key}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderColor: aimKey === opt.key ? 'primary.main' : 'divider',
                    bgcolor: aimKey === opt.key ? 'action.selected' : 'background.paper',
                  }}
                  onClick={() => setAimKey(opt.key)}
                >
                  <FormControlLabel
                    value={opt.key}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {opt.label}
                        </Typography>
                        {opt.description ? (
                          <Typography variant="body2" color="text.secondary">
                            {opt.description}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                    sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                  />
                </Paper>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        {aimKey === 'custom' ? (
          <TextField
            label="Describe what this conversation is for."
            value={aimCustomText}
            onChange={(e) => setAimCustomText(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        ) : null}

        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
            Who will use this?
          </FormLabel>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={audienceMode}
            onChange={(_, val) => {
              if (val) {
                setAudienceMode(val);
                if (val === 'staff') setCheckInEnabled(false);
              }
            }}
            aria-label="Audience mode"
          >
            <ToggleButton value="patient" sx={{ py: 1.5 }}>
              Patients (self-service)
            </ToggleButton>
            <ToggleButton value="staff" sx={{ py: 1.5 }}>
              Staff-assisted
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        {audienceMode === 'patient' ? (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={checkInEnabled}
                  onChange={(e) => setCheckInEnabled(e.target.checked)}
                />
              }
              label="Add to your facility's check-in process?"
            />
            {checkInEnabled ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                Patients checking in at your facility will be offered this screening before
                completing check-in.
              </Alert>
            ) : null}
          </Box>
        ) : null}

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Your shareable link
            </Typography>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setSlugEditing((v) => !v)}
            >
              {slugEditing ? 'Done' : 'Edit link name'}
            </Button>
          </Stack>
          {slugEditing ? (
            <TextField
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
              }}
              fullWidth
              helperText="Lowercase letters, numbers, and hyphens only (3–30 characters)"
              sx={{ mb: 1 }}
            />
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {shareUrl || 'Enter a name above to preview your link'}
          </Typography>
        </Box>

        <Button type="submit" variant="contained" size="large" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create & activate'}
        </Button>
      </Stack>
    </Paper>
  );
}

CareStartVariantForm.propTypes = {
  userData: PropTypes.object.isRequired,
  onCreated: PropTypes.func,
};
